import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { AuthService, CardGroup, RelatedCard, ShoppingCart } from '@ridango/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RelatedCardActions } from './+state/related-card.actions';
import { RelatedCardState } from './+state/related-card.reducer';
import { TranslateService } from '@ngx-translate/core';
import Env from '@ridango/core/env';
// import { RelatedCardState } from './store/related-card.store';
// import { RelatedCardActions } from './actions/related-card.actions';

@Injectable({
    providedIn: 'root'
})
export class RelatedCardService {
    constructor(
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private ngRedux: NgRedux<RelatedCardState>,
        private actions: RelatedCardActions,
        private translate: TranslateService,
        private authService: AuthService
    ) {}

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getRelatedCards(token: string = null): Observable<RelatedCard[]> {
        const url = this.env.IS_CORPORATE
            ? `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/company/${this.authService.getLoggedInUserCompanyId()}`
            : `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards`;

        return this.http.get(url).pipe(
            map((a: RelatedCard[]) => {
                a.map((rc: RelatedCard) => {
                    rc.wallet_balance = rc.wallet_balance / 100;
                });
                return a;
            })
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchCardGroups(token: string = null): Observable<CardGroup[]> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/groups`;
        return this.http.get(url).pipe(
            map((a: CardGroup[]) => {
                return a;
            })
        );
    }

    setAllRelatedCards(relatedCards: RelatedCard[]) {
        this.ngRedux.dispatch(this.actions.setAllRelatedCards(relatedCards));
    }

    setAllRelatedCardsNormalized(relatedCards: RelatedCard[]) {
        this.ngRedux.dispatch(this.actions.setAllRelatedCardsNormalized(relatedCards));
    }

    setSelectedCard(relatedCard: RelatedCard) {
        this.ngRedux.dispatch(this.actions.setSelectedRelatedCard(relatedCard));
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addRelatedCard(payload, token: string = null): Observable<RelatedCard[]> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards`;
        return this.http.post(url, payload).pipe(
            map((rc: RelatedCard[]) => {
                return rc;
            })
        );
    }

    createShoppingCartForPersonalizeBankCard(transactionId: string): Observable<ShoppingCart> {
        const url = `${this.env.CORE_API_URL}/partner/v3/region/${this.env.REGION_ID}/emv-card/personalization-cart`;
        return this.http
            .post(url, {
                transactionId,
                channel: 'web'
            })
            .pipe(
                map((rc: ShoppingCart) => {
                    return rc;
                })
            );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    editRelatedCard(cardId, payload, token: string = null): Observable<any> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/${cardId}/edit`;
        return this.http.put(url, payload); // returns ""
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deleteRelatedCard(cardId, token: string = null, companyId = null): Observable<any> {
        let url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/${cardId}`;
        if (companyId) {
            url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/${cardId}/company/${companyId}`;
        }

        return this.http.delete(url); // returns ""
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getCorporateRelatedCards(companyId: string, token: string = null): Observable<RelatedCard[]> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/company/${companyId}`;
        return this.http.get(url).pipe(
            map((a: RelatedCard[]) => {
                return a;
            })
        );
    }

    setAllCardsIds(relatedCardIds) {
        this.ngRedux.dispatch(this.actions.setAllCardsIds(relatedCardIds));
    }

    toggleSelectedRelatedCard(cardId) {
        this.ngRedux.dispatch(this.actions.toggleSelectedRelatedCard(cardId));
    }

    toggleAllRelatedCards() {
        this.ngRedux.dispatch(this.actions.toggleAllRelatedCards());
    }

    sortByNumber() {
        this.ngRedux.dispatch(this.actions.sortRelatedCards('card_nr'));
    }

    sortByName() {
        this.ngRedux.dispatch(this.actions.sortRelatedCards('name'));
    }

    resetRelatedCards() {
        this.ngRedux.dispatch(this.actions.resetRelatedCards());
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    changeRelatedCardPin(cardId, payload, token: string = null): Observable<RelatedCard[]> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/${cardId}/pin`;
        return this.http.put(url, payload).pipe(
            map((rc: RelatedCard[]) => {
                return rc;
            })
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeRelatedCardPin(cardId, token: string = null): Observable<any> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/${cardId}/pin`;
        return this.http.delete(url);
    }

    blockRelatedCard(cardNumber: string, sendNew: boolean = false): Observable<any> {
        // $payload[ 'card_nr' ] = $card_id;
        // $payload[ 'region_id' ] = $region_id;
        // $payload[ 'send_new' ] = true;
        const payload = {
            card_nr: cardNumber,
            region_id: this.env.REGION_ID,
            send_new: sendNew
        };
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/block`;
        let headers = new HttpHeaders();
        headers = this.addLanguageHeader(headers);

        return this.http.put(url, payload, { headers: headers });
    }
    unblockRelatedCard(cardNumber: string, sendNew: boolean = false) {
        const payload = {
            card_nr: cardNumber,
            region_id: this.env.REGION_ID,
            send_new: sendNew
        };
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/unblock`;
        return this.http.put(url, payload);
    }

    blockAndReplaceRelatedCard(payload): Observable<any> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/block`;
        payload.region_id = this.env.REGION_ID;
        let headers = new HttpHeaders();
        headers = this.addLanguageHeader(headers);

        return this.http.put(url, payload, { headers: headers });
    }

    addFavoriteJourney(payload) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/journeys/add`;
        return this.http.post(url, payload);
    }

    deleteFavoriteJourney(journeyId) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/journeys/${journeyId}/remove`;
        return this.http.delete(url);
    }

    getFavoriteJourneys() {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/journeys`;
        return this.http.get(url);
    }

    setFavoriteJourneys(journeys) {
        this.ngRedux.dispatch(this.actions.setFavoriteJourneys(journeys));
    }

    setSelectedFavoriteJourney(journey) {
        this.ngRedux.dispatch(this.actions.setSelectedFavoriteJourney(journey));
    }

    fetchCardStatus(cardNumber, cardType) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/related-cards/${cardNumber}/${cardType}/status`;
        return this.http.get(url);
    }

    registerSeutulippu(payload) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/tickets/request/seutulippu`;
        return this.http.put(url, payload);
    }

    private addLanguageHeader(headers: HttpHeaders) {
        const lang = this.translate.currentLang;
        if (lang !== '') {
            headers = headers.set('Accept-Language', lang);
        }

        return headers;
    }

    clearSelectedRelatedCard() {
        this.ngRedux.dispatch(this.actions.clearSelectedRelatedCard());
    }
}
